import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/PlansPage.css";
import { createCartItem } from "../api/cart";
import { getChallengesByMarket, getAccountTypes, getPrice } from "../api/challengePlans";

const marketOptions = [{ key: "forex", label: "فارکس" }];
const TOP_PROP_OPTION = { value: "top_prop", label: "تاچ پراپ بروکر را انتخاب کند" };
const USER_CHOICE_OPTION = { value: "user_choice", label: "من بروکر را انتخاب می‌کنم" };
const BROKER_SCOPE_TOUCHPROP_ONLY = "touchprop_only";
const BROKER_SCOPE_ALL = "all";

const formatMoney = (num) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(num);
const getBalanceOptions = (account) =>
  (account?.prices || [])
    .map((price) => Number(price.balance))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

const PlansPage = () => {
  const navigate = useNavigate();

  const market = "forex";
  const [challengeName, setChallengeName] = useState("");
  const [challengeList, setChallengeList] = useState([]);

  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [platform, setPlatform] = useState("MT5");

  const [priceInfo, setPriceInfo] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [planMessage, setPlanMessage] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const brokerTypeValue =
    selectedBroker === TOP_PROP_OPTION.value
      ? "1"
      : selectedBroker === USER_CHOICE_OPTION.value
      ? "2"
      : "";
  const brokerScopeValue =
    selectedBroker === TOP_PROP_OPTION.value
      ? BROKER_SCOPE_TOUCHPROP_ONLY
      : selectedBroker === USER_CHOICE_OPTION.value
      ? BROKER_SCOPE_ALL
      : "";
  const balanceOptions = useMemo(() => getBalanceOptions(selectedAccount), [selectedAccount]);

  // دریافت لیست چالش‌ها بر اساس بازار
  useEffect(() => {
    let cancelled = false;
    const loadChallenges = async () => {
      if (!brokerTypeValue) {
        setLoadingChallenges(false);
        setChallengeList([]);
        setChallengeName("");
        setAccountTypes([]);
        setSelectedAccount(null);
        setBalance(null);
        setPriceInfo(null);
        return;
      }
      setLoadingChallenges(true);
      try {
        const names = await getChallengesByMarket(market, brokerTypeValue, brokerScopeValue);
        if (cancelled) return;
        setChallengeList(names || []);
        if (names && names.length > 0) {
          setChallengeName(names[0]);
        } else {
          setChallengeName("");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("challenge list error", err);
        setChallengeList([]);
        setChallengeName("");
      } finally {
        if (!cancelled) setLoadingChallenges(false);
      }
    };
    loadChallenges();
    return () => {
      cancelled = true;
    };
  }, [market, brokerTypeValue, brokerScopeValue]);

  const stage = useMemo(() => {
    const name = (challengeName || "").replace(/\s+/g, "").toLowerCase();
    if (name.includes("دو") || name.includes("2")) return "two_stage";
    if (name.includes("سه") || name.includes("3")) return "three_stage";
    return "one_stage";
  }, [challengeName]);

  // دریافت انواع حساب بر اساس چالش انتخابی
  useEffect(() => {
    let cancelled = false;
    const loadAccountTypes = async () => {
      if (!challengeName) {
        setLoadingTypes(false);
        setAccountTypes([]);
        setSelectedAccount(null);
        setBalance(null);
        return;
      }
      setLoadingTypes(true);
      try {
        const res = await getAccountTypes({
          market_type: market,
          stage_type: stage,
          name: challengeName,
          broker_type: brokerTypeValue,
          broker_scope: brokerScopeValue,
        });
        if (cancelled) return;
        const list = res?.account_types || [];
        const firstAccount = list[0] || null;
        const firstBalance = getBalanceOptions(firstAccount)[0] || null;
        setAccountTypes(list);
        setSelectedAccount(firstAccount);
        setBalance(firstBalance);
      } catch (err) {
        if (cancelled) return;
        console.error("account types error", err);
        setAccountTypes([]);
        setSelectedAccount(null);
        setBalance(null);
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    };
    loadAccountTypes();
    return () => {
      cancelled = true;
    };
  }, [challengeName, market, stage, brokerTypeValue, brokerScopeValue]);

  // دریافت قیمت بر اساس انتخاب‌ها
  useEffect(() => {
    let cancelled = false;
    const loadPrice = async () => {
      if (!selectedAccount || !balance) {
        setLoadingPrice(false);
        setPriceInfo(null);
        return;
      }
      setLoadingPrice(true);
      try {
        const res = await getPrice({
          market_type: market,
          stage_type: stage,
          account_type_number: selectedAccount.account_type_number,
          balance,
          name: challengeName,
          broker_type: brokerTypeValue,
          broker_scope: brokerScopeValue,
        });
        if (cancelled) return;
        setPriceInfo(res);
      } catch (err) {
        if (cancelled) return;
        console.error("price error", err);
        setPriceInfo(null);
      } finally {
        if (!cancelled) setLoadingPrice(false);
      }
    };
    loadPrice();
    return () => {
      cancelled = true;
    };
  }, [selectedAccount, balance, stage, market, challengeName, brokerTypeValue, brokerScopeValue]);

  const summaryItems = useMemo(() => {
    const a = selectedAccount || {};
    const targets = [];
    if (a.stage1_target_percent !== null && a.stage1_target_percent !== undefined) {
      targets.push({ label: "هدف مرحله ۱", value: `${a.stage1_target_percent}%` });
    }
    if (a.stage2_target_percent !== null && a.stage2_target_percent !== undefined) {
      targets.push({ label: "هدف مرحله ۲", value: `${a.stage2_target_percent}%` });
    }
    if (a.stage3_target_percent !== null && a.stage3_target_percent !== undefined) {
      targets.push({ label: "هدف مرحله ۳", value: `${a.stage3_target_percent}%` });
    }

    return [
      { label: "حداکثر ضرر روزانه", value: a.daily_drawdown_percent ? `${a.daily_drawdown_percent}%` : "-" },
      { label: "حداکثر ضرر کل", value: a.total_drawdown_percent ? `${a.total_drawdown_percent}%` : "-" },
      ...targets,
      {
        label: "نوع بروکر",
        value:
          selectedBroker === TOP_PROP_OPTION.value
            ? "بروکر تاچ پراپ"
            : selectedBroker === USER_CHOICE_OPTION.value
            ? "بروکر شخصی"
            : "انتخاب نشده",
      },
      { label: "پلتفرم", value: platform },
      { label: "موجودی حساب", value: balance ? `${formatMoney(balance)} $` : "-" },
    ];
  }, [selectedAccount, platform, balance, selectedBroker]);

  const priceValue = priceInfo?.price?.price;

  const handleCreatePlan = async () => {
    if (!brokerTypeValue || !selectedAccount || !priceValue) {
      setPlanMessage("لطفاً نوع بروکر، نوع حساب و قیمت را انتخاب کنید.");
      return;
    }
    setPlanLoading(true);
    setPlanMessage("");
    try {
      await createCartItem({
        item_type: "challenge",
        name: `${challengeName || "چالش"} - نوع ${selectedAccount.account_type_number}`,
        market_type: market,
        investment_amount: balance,
        price: priceValue,
        platform_type: platform,
        broker_type: brokerTypeValue,
      });
      setPlanMessage("با موفقیت به سبد خرید اضافه شد.");
      setShowModal(false);
      navigate("/cart");
    } catch (err) {
      setPlanMessage(err?.message || "خطا در ثبت در سبد خرید");
    } finally {
      setPlanLoading(false);
    }
  };

  return (
    <MenuLayout>
      <div className="plans-page">
        <header className="plans-header">
          <h1 className="plans-title">پلن‌های سرمایه‌گذاری</h1>
          <p className="plans-subtitle">پلن مناسب را انتخاب کنید و با انتخاب نوع حساب، موجودی و پلتفرم ادامه دهید.</p>
        </header>

        <div className="plans-layout">
          <div className="plans-config">
            <div className="plans-config-title">تنظیمات پلن</div>

            <div className="plans-row plans-broker-selector">
              <span className="plans-row-label">ابتدا نوع بروکر را انتخاب کنید:</span>
              <div className="plans-broker-tabs">
                {[TOP_PROP_OPTION, USER_CHOICE_OPTION].map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`plans-broker-tab ${selectedBroker === option.value ? "plans-broker-tab-active" : ""}`}
                    onClick={() => {
                      setSelectedBroker(option.value);
                      setChallengeName("");
                      setChallengeList([]);
                      setAccountTypes([]);
                      setSelectedAccount(null);
                      setBalance(null);
                      setPriceInfo(null);
                      setPlanMessage("");
                      setShowModal(false);
                    }}
                  >
                    <span>{option.label}</span>
                    <small>
                      {option.value === TOP_PROP_OPTION.value
                        ? "فقط چالش‌های قابل ارائه برای بروکر تاچ پراپ"
                        : "فقط چالش‌های قابل ارائه برای همه بروکرها"}
                    </small>
                  </button>
                ))}
              </div>
            </div>

            <div className="plans-row">
              <span className="plans-row-label">بازار مورد نظر:</span>
              <div className="plans-chip-group">
                {marketOptions.map((v) => (
                  <button key={v.key} className={`chip ${market === v.key ? "chip-active" : ""}`} disabled>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="plans-row">
              <span className="plans-row-label">نام چالش:</span>
              <div className="plans-chip-group">
                {!selectedBroker && <span className="plans-hint">ابتدا نوع بروکر را انتخاب کنید</span>}
                {loadingChallenges && <span className="plans-hint">در حال بارگذاری ...</span>}
                {selectedBroker &&
                  !loadingChallenges &&
                  (challengeList || []).map((name) => (
                    <button key={name} className={`chip ${challengeName === name ? "chip-active" : ""}`} onClick={() => setChallengeName(name)}>
                      {name}
                    </button>
                  ))}
                {selectedBroker &&
                  !loadingChallenges &&
                  (!challengeList || challengeList.length === 0) && (
                    <span className="plans-hint">برای این نوع بروکر چالشی پیدا نشد</span>
                  )}
              </div>
            </div>

            <div className="plans-row">
              <span className="plans-row-label">دراودان:</span>
              <div className="plans-chip-group">
                {loadingTypes && <span className="plans-hint">در حال دریافت انواع حساب ...</span>}
                {!loadingTypes &&
                  accountTypes.map((t) => {
                    const totalDrawdown = t.total_drawdown_percent ? `${t.total_drawdown_percent}%` : null;
                    const dailyDrawdown = t.daily_drawdown_percent ? `${t.daily_drawdown_percent}%` : null;
                    return (
                      <button
                        key={t.account_type_number}
                        className={`chip ${selectedAccount?.account_type_number === t.account_type_number ? "chip-active" : ""}`}
                        onClick={() => {
                          setSelectedAccount(t);
                          setBalance(getBalanceOptions(t)[0] || null);
                        }}
                      >
                        {totalDrawdown ? `دراودان کل ${totalDrawdown}` : `نوع ${t.account_type_number}`}
                        {dailyDrawdown ? ` | روزانه ${dailyDrawdown}` : ""}
                      </button>
                    );
                  })}
                {selectedBroker && !loadingTypes && accountTypes.length === 0 && (
                  <span className="plans-hint">نوع حسابی یافت نشد</span>
                )}
              </div>
            </div>

            <div className="plans-row">
              <span className="plans-row-label">موجودی مورد نظر:</span>
              <div className="plans-chip-group plans-money">
                {!selectedAccount && <span className="plans-hint">ابتدا نوع حساب را انتخاب کنید</span>}
                {selectedAccount && balanceOptions.length === 0 && (
                  <span className="plans-hint">موجودی برای این نوع حساب تعریف نشده است</span>
                )}
                {balanceOptions.map((amt) => (
                  <button
                    key={amt}
                    className={`chip money-chip ${balance === amt ? "chip-active" : ""}`}
                    onClick={() => setBalance(amt)}
                  >
                    {formatMoney(amt)} $
                  </button>
                ))}
              </div>
            </div>

            <div className="plans-row">
              <span className="plans-row-label">پلتفرم معاملاتی:</span>
              <div className="plans-platforms">
                {[
                  { key: "MT5", img: "/images/mt5.jpeg", label: "MT5" },
                ].map((p) => (
                  <button
                    key={p.key}
                    className={`platform-card ${platform === p.key ? "platform-card-active" : ""}`}
                    onClick={() => setPlatform(p.key)}
                  >
                    <img src={p.img} alt={p.label} />
                    <span>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="plans-summary-card">
            <div className="plans-summary-title">خلاصه پلن انتخابی</div>
            <div className="plans-summary-list">
              {summaryItems.map((item, idx) => (
                <div className="plans-summary-row" key={idx}>
                  <span className="plans-summary-label">{item.label}</span>
                  <span className="plans-summary-value">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="plans-summary-footer">
              <div className="plans-summary-price">
                <span className="plans-summary-price-label">قیمت نهایی:</span>
                <span className="plans-summary-price-value">
                  {loadingPrice ? "در حال محاسبه..." : priceValue ? `$${formatMoney(priceValue)}` : "نامشخص"}
                </span>
              </div>
              {planMessage && <div className="plans-message">{planMessage}</div>}
            </div>
            <button
              className="plans-summary-cta"
              onClick={() => setShowModal(true)}
              disabled={!selectedBroker || !priceValue}
            >
              ثبت و ادامه
            </button>
          </div>
        </div>

        {showModal && (
          <div className="plans-modal-backdrop" onClick={() => setShowModal(false)}>
            <div className="plans-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="plans-modal-title">خلاصه خرید</h3>
              <div className="plans-modal-table">
                <div className="plans-modal-row head">
                  <span>عنوان</span>
                  <span>مقدار</span>
                </div>
                {summaryItems.map((item, idx) => (
                  <div className="plans-modal-row" key={idx}>
                    <span>{item.label}</span>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="plans-modal-footer">
                <div className="plans-modal-price">قیمت: {priceValue ? `$${formatMoney(priceValue)}` : "نامشخص"}</div>
                <button className="plans-modal-cta" onClick={handleCreatePlan} disabled={planLoading || !priceValue}>
                  نهایی کردن خرید
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MenuLayout>
  );
};

export default PlansPage;
